import React, { useMemo, useState, useRef, useCallback } from 'react'
import { scaleDiverging } from 'd3-scale'
import { interpolateRdBu } from 'd3-scale-chromatic'
import {
  calculateCorrelationMatrix,
  correlationToDistance,
  hierarchicalClusteringOrder,
  reorderMatrix,
  reorderLabels,
  extractSubDimensionScores,
  SUB_DIMENSION_META,
  SUB_DIMENSION_KEYS,
  PARENT_COLORS,
  pearsonCorrelation
} from '../lib/correlation'

// Cell size and spacing
const CELL_SIZE = 64
const LABEL_WIDTH = 120
const LABEL_HEIGHT = 80
const MARGIN = { top: LABEL_HEIGHT, left: LABEL_WIDTH, right: 40, bottom: 20 }

/**
 * Tooltip component for correlation details
 */
const CorrelationTooltip = ({ data, position, poloScores }) => {
  if (!data) return null

  const { row, col, value, rowMeta, colMeta } = data

  // Calculate which raw indicators drive this correlation
  const getTopIndicatorPairs = () => {
    // This would require access to raw aggregate data
    // For now, show the basic correlation info
    return null
  }

  return (
    <div
      className="fixed bg-white text-gray-900 p-3 rounded-lg shadow-lg border"
      style={{
        left: position.x + 10,
        top: position.y + 10,
        zIndex: 1050,
        maxWidth: 320,
        fontSize: '0.85rem',
        pointerEvents: 'none'
      }}
    >
      <div className="font-bold mb-2 border-b pb-2">
        {rowMeta.fullLabel} × {colMeta.fullLabel}
      </div>

      <div className="flex items-center gap-2 mb-2">
        <span className="text-gray-500">Correlação:</span>
        <span className={`font-bold ${Math.abs(value) >= 0.5 ? 'text-green-600' : Math.abs(value) >= 0.3 ? 'text-yellow-600' : 'text-gray-500'}`}>
          r = {value.toFixed(3)}
        </span>
      </div>

      <div className="text-sm text-gray-500 mb-2">
        <div className="flex items-center gap-1 mb-1">
          <span
            className="rounded"
            style={{
              width: 10,
              height: 10,
              backgroundColor: PARENT_COLORS[rowMeta.parent]
            }}
          />
          {rowMeta.description}
        </div>
        <div className="flex items-center gap-1">
          <span
            className="rounded"
            style={{
              width: 10,
              height: 10,
              backgroundColor: PARENT_COLORS[colMeta.parent]
            }}
          />
          {colMeta.description}
        </div>
      </div>

      <div className="text-sm border-t pt-2 mt-2 text-gray-500">
        <i className="fa fa-info-circle mr-1" />
        {(() => {
          const absValue = Math.abs(value)
          const direction = value >= 0 ? 'positiva' : 'negativa'
          const directionExplanation = value >= 0
            ? 'quando uma aumenta, a outra também tende a aumentar'
            : 'quando uma aumenta, a outra tende a diminuir'

          if (absValue >= 0.7) {
            return `Correlação ${direction} forte: ${directionExplanation}`
          } else if (absValue >= 0.4) {
            return `Correlação ${direction} moderada: ${directionExplanation}`
          } else if (absValue >= 0.2) {
            return `Correlação ${direction} fraca: alguma relação entre as dimensões`
          } else {
            return 'Correlação muito fraca: dimensões praticamente independentes'
          }
        })()}
      </div>
    </div>
  )
}

/**
 * Scatter plot modal for drill-down
 */
const ScatterPlotModal = ({ data, onClose, poloScores }) => {
  const [hoveredPolo, setHoveredPolo] = useState(null)

  if (!data) return null

  const { rowKey, colKey, rowMeta, colMeta } = data

  const xValues = poloScores.map(p => p[colKey])
  const yValues = poloScores.map(p => p[rowKey])

  const xMin = Math.min(...xValues)
  const xMax = Math.max(...xValues)
  const yMin = Math.min(...yValues)
  const yMax = Math.max(...yValues)

  const padding = 40
  const plotWidth = 500
  const plotHeight = 400

  const scaleX = (v) => padding + ((v - xMin) / (xMax - xMin || 1)) * (plotWidth - 2 * padding)
  const scaleY = (v) => plotHeight - padding - ((v - yMin) / (yMax - yMin || 1)) * (plotHeight - 2 * padding)

  const correlation = pearsonCorrelation(xValues, yValues)

  // Calculate trend line
  const meanX = xValues.reduce((a, b) => a + b, 0) / xValues.length
  const meanY = yValues.reduce((a, b) => a + b, 0) / yValues.length
  const slope = xValues.reduce((acc, x, i) => acc + (x - meanX) * (yValues[i] - meanY), 0) /
    xValues.reduce((acc, x) => acc + (x - meanX) ** 2, 0)
  const intercept = meanY - slope * meanX

  const trendX1 = xMin
  const trendX2 = xMax
  const trendY1 = slope * trendX1 + intercept
  const trendY2 = slope * trendX2 + intercept

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-lg p-4"
        style={{ maxWidth: 600 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <h5 className="mb-0 font-semibold">
            {rowMeta.fullLabel} × {colMeta.fullLabel}
          </h5>
          <button className="px-2 py-1 text-sm border border-gray-400 rounded hover:bg-gray-100" onClick={onClose}>
            <i className="fa fa-times" />
          </button>
        </div>

        <div className="text-center mb-2">
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-500 text-white">r = {correlation.toFixed(3)}</span>
        </div>

        <svg width={plotWidth} height={plotHeight} className="block mx-auto">
          {/* Grid lines */}
          {[0, 2.5, 5, 7.5, 10].map(v => (
            <g key={`grid-${v}`}>
              <line
                x1={padding}
                y1={scaleY(v)}
                x2={plotWidth - padding}
                y2={scaleY(v)}
                stroke="#eee"
                strokeDasharray="3,3"
              />
              <line
                x1={scaleX(v)}
                y1={padding}
                x2={scaleX(v)}
                y2={plotHeight - padding}
                stroke="#eee"
                strokeDasharray="3,3"
              />
            </g>
          ))}

          {/* Axes */}
          <line x1={padding} y1={plotHeight - padding} x2={plotWidth - padding} y2={plotHeight - padding} stroke="#333" />
          <line x1={padding} y1={padding} x2={padding} y2={plotHeight - padding} stroke="#333" />

          {/* Trend line */}
          <line
            x1={scaleX(trendX1)}
            y1={scaleY(trendY1)}
            x2={scaleX(trendX2)}
            y2={scaleY(trendY2)}
            stroke="#999"
            strokeWidth={2}
            strokeDasharray="5,5"
          />

          {/* Data points */}
          {poloScores.map((polo, i) => (
            <g key={polo.id}>
              <circle
                cx={scaleX(polo[colKey])}
                cy={scaleY(polo[rowKey])}
                r={hoveredPolo?.id === polo.id ? 9 : 6}
                fill={PARENT_COLORS[rowMeta.parent]}
                fillOpacity={hoveredPolo?.id === polo.id ? 1 : 0.7}
                stroke={hoveredPolo?.id === polo.id ? '#333' : 'white'}
                strokeWidth={hoveredPolo?.id === polo.id ? 2 : 1}
                style={{ cursor: 'pointer', transition: 'all 0.15s ease' }}
                onMouseEnter={() => setHoveredPolo(polo)}
                onMouseLeave={() => setHoveredPolo(null)}
              />
            </g>
          ))}

          {/* Hover label */}
          {hoveredPolo && (
            <g>
              <rect
                x={scaleX(hoveredPolo[colKey]) - 4}
                y={scaleY(hoveredPolo[rowKey]) - 36}
                width={Math.max(hoveredPolo.nome.length * 7 + 16, 80)}
                height={24}
                rx={4}
                fill="white"
                stroke="#333"
                strokeWidth={1}
                style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}
              />
              <text
                x={scaleX(hoveredPolo[colKey]) + 4}
                y={scaleY(hoveredPolo[rowKey]) - 20}
                fontSize={12}
                fontWeight={600}
                fill="#333"
              >
                {hoveredPolo.nome}
              </text>
            </g>
          )}

          {/* Axis labels */}
          <text x={plotWidth / 2} y={plotHeight - 5} textAnchor="middle" fontSize={12} fill="#666">
            {colMeta.fullLabel}
          </text>
          <text
            x={15}
            y={plotHeight / 2}
            textAnchor="middle"
            fontSize={12}
            fill="#666"
            transform={`rotate(-90, 15, ${plotHeight / 2})`}
          >
            {rowMeta.fullLabel}
          </text>
        </svg>

        <div className="text-center mt-3 text-sm text-gray-500">
          Cada ponto representa um polo. Clique fora para fechar.
        </div>
      </div>
    </div>
  )
}

/**
 * Main Correlation Matrix Component
 */
export default function CorrelationMatrix({ calculatedPolos, nivelFilter }) {
  const [tooltip, setTooltip] = useState(null)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [scatterData, setScatterData] = useState(null)
  const svgRef = useRef(null)

  // Filter polos by nivel
  const filteredPolos = useMemo(() => {
    if (!nivelFilter || nivelFilter.size === 0) return calculatedPolos
    return calculatedPolos.filter(p => nivelFilter.has(p.nivel))
  }, [calculatedPolos, nivelFilter])

  // Extract sub-dimension scores
  const poloScores = useMemo(() => {
    return extractSubDimensionScores(filteredPolos)
  }, [filteredPolos])

  // Calculate correlation matrix
  const { matrix, orderedLabels, orderedKeys } = useMemo(() => {
    if (poloScores.length < 3) {
      return { matrix: [], orderedLabels: [], orderedKeys: [] }
    }

    const { matrix: rawMatrix, labels } = calculateCorrelationMatrix(poloScores, SUB_DIMENSION_KEYS)

    // Cluster for dendrogram ordering
    const distMatrix = correlationToDistance(rawMatrix)
    const order = hierarchicalClusteringOrder(distMatrix)

    // Reorder matrix and labels
    const reorderedMatrix = reorderMatrix(rawMatrix, order)
    const reorderedKeys = reorderLabels(labels, order)
    const reorderedLabels = reorderedKeys.map(k => SUB_DIMENSION_META[k])

    return {
      matrix: reorderedMatrix,
      orderedLabels: reorderedLabels,
      orderedKeys: reorderedKeys
    }
  }, [poloScores])

  // Color scale (diverging: blue = negative, white = 0, red = positive)
  const colorScale = useMemo(() => {
    return scaleDiverging(t => interpolateRdBu(1 - t)).domain([-1, 0, 1])
  }, [])

  const handleCellHover = useCallback((e, row, col, value) => {
    const rect = e.target.getBoundingClientRect()
    setTooltipPosition({ x: rect.right, y: rect.top })
    setTooltip({
      row,
      col,
      value,
      rowMeta: orderedLabels[row],
      colMeta: orderedLabels[col]
    })
  }, [orderedLabels])

  const handleCellLeave = useCallback(() => {
    setTooltip(null)
  }, [])

  const handleCellClick = useCallback((row, col) => {
    if (row === col) return
    setScatterData({
      rowKey: orderedKeys[row],
      colKey: orderedKeys[col],
      rowMeta: orderedLabels[row],
      colMeta: orderedLabels[col]
    })
  }, [orderedKeys, orderedLabels])

  if (poloScores.length < 3) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded px-4 py-3">
        <i className="fa fa-exclamation-triangle mr-2" />
        Selecione pelo menos 3 polos para visualizar a matriz de correlação.
      </div>
    )
  }

  const n = matrix.length
  const width = MARGIN.left + n * CELL_SIZE + MARGIN.right
  const height = MARGIN.top + n * CELL_SIZE + MARGIN.bottom

  return (
    <div className="correlation-matrix-container">
      {/* Legend */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">Correlação:</span>
          <div className="flex items-center">
            <span className="text-sm mr-1">-1</span>
            <svg width={120} height={16}>
              <defs>
                <linearGradient id="corr-gradient">
                  <stop offset="0%" stopColor={colorScale(-1)} />
                  <stop offset="50%" stopColor={colorScale(0)} />
                  <stop offset="100%" stopColor={colorScale(1)} />
                </linearGradient>
              </defs>
              <rect x={0} y={0} width={120} height={16} fill="url(#corr-gradient)" rx={3} />
            </svg>
            <span className="text-sm ml-1">+1</span>
          </div>
        </div>
        <div className="flex gap-3">
          {Object.entries({ connectivity: 'Conectividade', diversity: 'Diversidade', comfort: 'Conforto' }).map(([key, label]) => (
            <div key={key} className="flex items-center gap-1 text-sm">
              <span
                className="rounded"
                style={{ width: 12, height: 12, backgroundColor: PARENT_COLORS[key] }}
              />
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Matrix SVG */}
      <div className="overflow-auto">
        <svg ref={svgRef} width={width} height={height}>
          {/* Column labels (top) */}
          {orderedLabels.map((meta, i) => (
            <g key={`col-label-${i}`} transform={`translate(${MARGIN.left + i * CELL_SIZE + CELL_SIZE / 2}, ${MARGIN.top - 10})`}>
              <text
                transform="rotate(-45)"
                textAnchor="start"
                fontSize={11}
                fill={PARENT_COLORS[meta.parent]}
                fontWeight={500}
              >
                {meta.label}
              </text>
            </g>
          ))}

          {/* Row labels (left) */}
          {orderedLabels.map((meta, i) => (
            <g key={`row-label-${i}`} transform={`translate(${MARGIN.left - 8}, ${MARGIN.top + i * CELL_SIZE + CELL_SIZE / 2})`}>
              <text
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={11}
                fill={PARENT_COLORS[meta.parent]}
                fontWeight={500}
              >
                {meta.label}
              </text>
            </g>
          ))}

          {/* Matrix cells */}
          {matrix.map((row, i) =>
            row.map((value, j) => {
              const isDiagonal = i === j
              const x = MARGIN.left + j * CELL_SIZE
              const y = MARGIN.top + i * CELL_SIZE

              return (
                <g key={`cell-${i}-${j}`}>
                  <rect
                    x={x}
                    y={y}
                    width={CELL_SIZE}
                    height={CELL_SIZE}
                    fill={isDiagonal ? '#f8f9fa' : colorScale(value)}
                    stroke="#fff"
                    strokeWidth={2}
                    rx={4}
                    style={{ cursor: isDiagonal ? 'default' : 'pointer' }}
                    onMouseEnter={(e) => !isDiagonal && handleCellHover(e, i, j, value)}
                    onMouseLeave={handleCellLeave}
                    onClick={() => handleCellClick(i, j)}
                  />
                  <text
                    x={x + CELL_SIZE / 2}
                    y={y + CELL_SIZE / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={isDiagonal ? 10 : 12}
                    fontWeight={isDiagonal ? 400 : 600}
                    fill={isDiagonal ? '#999' : Math.abs(value) > 0.5 ? '#fff' : '#333'}
                    style={{ pointerEvents: 'none' }}
                  >
                    {isDiagonal ? '1.00' : value.toFixed(2)}
                  </text>
                </g>
              )
            })
          )}

          {/* Parent group brackets */}
          {(() => {
            const groups = []
            let currentParent = null
            let groupStart = 0

            orderedLabels.forEach((meta, i) => {
              if (meta.parent !== currentParent) {
                if (currentParent !== null) {
                  groups.push({
                    parent: currentParent,
                    start: groupStart,
                    end: i - 1
                  })
                }
                currentParent = meta.parent
                groupStart = i
              }
            })
            groups.push({
              parent: currentParent,
              start: groupStart,
              end: orderedLabels.length - 1
            })

            return groups.map((group, gi) => {
              const x1 = MARGIN.left + group.start * CELL_SIZE
              const x2 = MARGIN.left + (group.end + 1) * CELL_SIZE
              const y = MARGIN.top + n * CELL_SIZE + 5

              return (
                <g key={`group-${gi}`}>
                  <line
                    x1={x1}
                    y1={y}
                    x2={x2}
                    y2={y}
                    stroke={PARENT_COLORS[group.parent]}
                    strokeWidth={3}
                    strokeLinecap="round"
                  />
                </g>
              )
            })
          })()}
        </svg>
      </div>

      {/* Info */}
      <div className="bg-gray-50 border border-gray-200 rounded px-4 py-3 text-sm mt-3">
        <i className="fa fa-info-circle mr-2 text-blue-500" />
        <strong>Como interpretar:</strong> Valores próximos de +1 indicam que as dimensões variam juntas
        (quando uma aumenta, a outra também). Valores próximos de 0 indicam independência.
        A ordenação agrupa dimensões similares. Clique em uma célula para ver o gráfico de dispersão.
      </div>

      {/* Tooltip */}
      <CorrelationTooltip
        data={tooltip}
        position={tooltipPosition}
        poloScores={poloScores}
      />

      {/* Scatter plot modal */}
      {scatterData && (
        <ScatterPlotModal
          data={scatterData}
          onClose={() => setScatterData(null)}
          poloScores={poloScores}
        />
      )}
    </div>
  )
}
